import axios, { AxiosInstance, AxiosResponse } from 'axios';
import md5 from 'md5';
import { encodeURIJson, isObject } from '@tencent/txv-utils';
import { iwanBaseUrl, addTimeParam } from '../middleware';
import { IwanRequestConfig } from '../interface';
import { setTraceparentInHeader } from '../helper';

interface ReqList {
  // eslint-disable-next-line
  [md5Key: string]: any,
}
const reqList: ReqList = {};
const getUniqueKey = (url: string, params?: object | string): string => {
  const key = `${url}_${isObject(params) ? encodeURIJson(params) : params}`;
  return md5(key);
};

/**
 * 创建axios实例，扩展get和post的unique参数功能
 */
interface IwanRequest extends AxiosInstance {
  // eslint-disable-next-line
  get: <T = any, R = AxiosResponse<T>, D = any>(url: string, config?: IwanRequestConfig<D>) => Promise<R>
  // eslint-disable-next-line
  post: <T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: IwanRequestConfig<D>) => Promise<R>
}
const iwanRequest: IwanRequest = axios.create({
  withCredentials: true,
});
const originGet = iwanRequest.get;
const originPost = iwanRequest.post;

// eslint-disable-next-line
iwanRequest.get = <T = any, R = AxiosResponse<T>, D = any>(url: string, config?: IwanRequestConfig<D>):
Promise<R> => {
  const { params, unique } = config || {};
  const uniqueKey = unique ? getUniqueKey(url, params) : '';
  if (uniqueKey && reqList[uniqueKey]) {
    return reqList[uniqueKey];
  }
  const req = originGet<T, R, D>(url, {
    ...config,
    headers: setTraceparentInHeader(url, config?.headers),
  });
  if (uniqueKey) {
    reqList[uniqueKey] = req;
    req.finally(() => {
      delete reqList[uniqueKey];
    });
  }

  return req;
};
// eslint-disable-next-line
iwanRequest.post = <T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: IwanRequestConfig<D>):
Promise<R> => {
  const { params, unique } = config || {};
  const uniqueKey = unique ? getUniqueKey(url, { ...data, ...params }) : '';
  if (uniqueKey && reqList[uniqueKey]) {
    return reqList[uniqueKey];
  }
  const req = originPost<T, R, D>(url, data, {
    ...config,
    headers: setTraceparentInHeader(url, config?.headers),
  });
  if (uniqueKey) {
    reqList[uniqueKey] = req;
    req.finally(() => {
      delete reqList[uniqueKey];
    });
  }

  return req;
};

// baseUrl多端域名处理
iwanRequest.interceptors.request.use(iwanBaseUrl);
// cache:false强行增加时间戳
iwanRequest.interceptors.request.use(addTimeParam);
// 返回axios包装后的data，即为业务接口的返回内容||{}
iwanRequest.interceptors.response.use(response => response.data);

export {
  iwanRequest,
};
